import React, { useEffect, useState, useCallback } from "react";
import axios from "axios";
import { Trash2, Mail, Phone, Calendar, Search, RefreshCw } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const getHeaders = () => ({ Authorization: `Bearer ${localStorage.getItem("admin_token")}` });

export default function AdminEnquiries() {
  const [items, setItems] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await axios.get(`${BACKEND_URL}/api/admin/enquiries`, { headers: getHeaders() });
      setItems(res.data);
      setFiltered(res.data);
    } catch (e) { console.error(e); }
    setLoading(false);
  }, []);

  useEffect(() => { load(); }, [load]);

  useEffect(() => {
    const q = search.toLowerCase();
    setFiltered(items.filter((e) =>
      e.full_name.toLowerCase().includes(q) || e.course.toLowerCase().includes(q) ||
      e.phone.includes(q) || e.email.toLowerCase().includes(q)
    ));
  }, [search, items]);

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this enquiry?")) return;
    try {
      await axios.delete(`${BACKEND_URL}/api/admin/enquiries/${id}`, { headers: getHeaders() });
      setItems((prev) => prev.filter((i) => i.id !== id));
      if (selected?.id === id) setSelected(null);
    } catch { alert("Failed to delete."); }
  };

  const fmt = (iso) => new Date(iso).toLocaleString("en-IN", { day: "2-digit", month: "short", year: "numeric", hour: "2-digit", minute: "2-digit" });

  if (loading) return <div className="flex items-center justify-center h-64 text-gray-400">Loading enquiries...</div>;

  return (
    <div data-testid="admin-enquiries-page" className="space-y-5">
      <div className="flex items-center justify-between flex-wrap gap-3">
        <div>
          <h2 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>Enquiries</h2>
          <p className="text-sm text-gray-400 mt-0.5">{items.length} total enquiries received</p>
        </div>
        <button onClick={load} className="flex items-center gap-2 text-sm font-semibold text-gray-500 hover:text-gray-700 px-3 py-2 rounded-lg border border-gray-200 hover:bg-gray-50">
          <RefreshCw size={14} /> Refresh
        </button>
      </div>

      {/* Search */}
      <div className="relative">
        <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" />
        <input type="text" value={search} onChange={(e) => setSearch(e.target.value)}
          placeholder="Search by name, course, phone..."
          className="w-full pl-9 pr-4 py-2.5 border border-gray-200 rounded-xl text-sm focus:outline-none focus:border-blue-900 bg-white" />
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-5">
        {/* List */}
        <div className="lg:col-span-1 space-y-3 max-h-[600px] overflow-y-auto pr-1">
          {filtered.length === 0 ? (
            <div className="text-center py-10 bg-white rounded-2xl border border-dashed border-gray-200">
              <Mail size={36} className="mx-auto text-gray-300 mb-2" />
              <p className="text-sm text-gray-400">No enquiries found</p>
            </div>
          ) : (
            filtered.map((e, i) => (
              <div key={e.id}
                onClick={() => setSelected(e)}
                className={`bg-white rounded-xl p-4 border cursor-pointer transition-all hover:shadow-sm ${selected?.id === e.id ? "border-blue-900 shadow-sm" : "border-gray-100"}`}
                data-testid={`enquiry-item-${i}`}>
                <div className="flex items-center justify-between mb-1">
                  <span className="font-bold text-sm" style={{ color: "#0A2540" }}>{e.full_name}</span>
                  <button onClick={(ev) => { ev.stopPropagation(); handleDelete(e.id); }} className="p-1 rounded hover:bg-red-50 text-red-400">
                    <Trash2 size={13} />
                  </button>
                </div>
                <div className="text-xs font-semibold text-yellow-600 mb-1">{e.course}</div>
                <div className="text-xs text-gray-400">{fmt(e.created_at)}</div>
              </div>
            ))
          )}
        </div>

        {/* Detail */}
        <div className="lg:col-span-2">
          {selected ? (
            <div className="bg-white rounded-2xl border border-gray-100 shadow-sm p-6" data-testid="enquiry-detail">
              <div className="flex items-start justify-between mb-5">
                <div>
                  <h3 className="text-xl font-extrabold" style={{ color: "#0A2540", fontFamily: "Manrope, sans-serif" }}>{selected.full_name}</h3>
                  <span className="text-sm font-bold" style={{ color: "#F2C94C" }}>{selected.course}</span>
                </div>
                <button onClick={() => handleDelete(selected.id)} className="flex items-center gap-1.5 text-xs font-semibold text-red-500 hover:text-red-700 px-3 py-2 rounded-lg border border-red-100 hover:bg-red-50">
                  <Trash2 size={13} /> Delete
                </button>
              </div>

              <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 mb-5">
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                  <Mail size={16} color="#F2C94C" />
                  <div>
                    <div className="text-xs text-gray-400">Email</div>
                    <a href={`mailto:${selected.email}`} className="text-sm font-semibold" style={{ color: "#0A2540" }}>{selected.email}</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50">
                  <Phone size={16} color="#F2C94C" />
                  <div>
                    <div className="text-xs text-gray-400">Phone</div>
                    <a href={`tel:${selected.phone}`} className="text-sm font-semibold" style={{ color: "#0A2540" }}>{selected.phone}</a>
                  </div>
                </div>
                <div className="flex items-center gap-3 p-3 rounded-xl bg-gray-50 sm:col-span-2">
                  <Calendar size={16} color="#F2C94C" />
                  <div>
                    <div className="text-xs text-gray-400">Received</div>
                    <div className="text-sm font-semibold" style={{ color: "#0A2540" }}>{fmt(selected.created_at)}</div>
                  </div>
                </div>
              </div>

              {selected.message && (
                <div className="bg-gray-50 rounded-xl p-4 border-l-4" style={{ borderLeftColor: "#F2C94C" }}>
                  <div className="text-xs font-bold text-gray-400 mb-2 uppercase tracking-wide">Message</div>
                  <p className="text-sm text-gray-600 leading-relaxed">{selected.message}</p>
                </div>
              )}

              <div className="flex gap-3 mt-5">
                <a href={`mailto:${selected.email}?subject=Re: Your Enquiry for ${selected.course} – Trident Academy`}
                  className="flex-1 text-center py-2.5 rounded-xl text-sm font-bold text-white hover:opacity-90 transition-all"
                  style={{ backgroundColor: "#0A2540" }}>
                  Reply via Email
                </a>
                <a href={`tel:${selected.phone}`}
                  className="flex-1 text-center py-2.5 rounded-xl text-sm font-bold border-2 hover:bg-gray-50 transition-all"
                  style={{ borderColor: "#0A2540", color: "#0A2540" }}>
                  Call Now
                </a>
              </div>
            </div>
          ) : (
            <div className="bg-white rounded-2xl border border-dashed border-gray-200 flex items-center justify-center h-64">
              <p className="text-sm text-gray-400">Select an enquiry to view details</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
